import { useEffect, useId, useMemo, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { toast } from 'sonner';
import { cn } from '@/lib/cn';
import { Button } from '@/components/common/Button';
import { CompanyDetailsPanel, isCompanyDetailsValid } from './CompanyDetailsPanel';
import { AccountUsagePanel } from './AccountUsagePanel';
import {
  IndividualsPanel,
  describeIndividualsPanelGap,
  isIndividualsPanelValid,
} from './IndividualsPanel';
import { emptyFormState, type DraftFormState } from './types';
import { useClientDraft } from './useClientDraft';

interface Props {
  open: boolean;
  resumeDraftId?: number | null;
  onClose: () => void;
  onCreated: () => void;
}

const STEPS = [
  { id: 1, label: 'Company details' },
  { id: 2, label: 'Account usage' },
  { id: 3, label: 'Individuals' },
] as const;

type StepId = 1 | 2 | 3;

export function NewClientModal({ open, resumeDraftId, onClose, onCreated }: Props) {
  const [step, setStep] = useState<StepId>(1);
  const [form, setForm] = useState<DraftFormState>(() => emptyFormState());
  const [isLoadingDraft, setIsLoadingDraft] = useState(false);
  const [loadDraftFailed, setLoadDraftFailed] = useState(false);
  // Tracks whether the form has changed since the last save (or initial
  // open / draft load). Suppresses the close-confirmation dialog when
  // there is nothing to lose. False positives (user types and types back)
  // are acceptable — the prompt is non-destructive.
  const [isDirty, setIsDirty] = useState(false);
  // Counter incremented on every panel edit. Snapshotted before each
  // saveDraft so a successful save only clears isDirty when no edits
  // landed during the in-flight request.
  const changeRevisionRef = useRef(0);
  // Inline feedback for the Save-as-draft button. Replaces the previous
  // toast.warning / toast.success / toast.error trio, which were firing
  // but hidden behind the modal's Radix overlay. Cleared on next panel
  // edit, on modal open/close, and on step change so stale feedback never
  // lingers in a context the operator has moved on from.
  type SaveMessage = { kind: 'warning' | 'success' | 'error'; text: string };
  const [saveMessage, setSaveMessage] = useState<SaveMessage | null>(null);
  // Unique id for the Submit gap hint so multiple modal instances don't
  // collide on a hard-coded literal id (the aria-describedby relation
  // would otherwise point at the wrong hint span).
  const submitGapHintId = useId();
  const {
    clientId,
    isSaving,
    isSubmitting,
    fieldErrors,
    saveDraft,
    submit,
    loadDraft,
    setClientId,
  } = useClientDraft();

  useEffect(() => {
    if (!open) {
      setStep(1);
      setForm(emptyFormState());
      setClientId(null);
      setLoadDraftFailed(false);
      setIsDirty(false);
      setSaveMessage(null);
      return;
    }
    if (resumeDraftId == null) {
      setIsDirty(false);
      setSaveMessage(null);
      return;
    }
    // Guard against stale results: if the modal closes or resumeDraftId
    // changes before loadDraft resolves, we must not overwrite the new
    // state with the previous draft's payload.
    let cancelled = false;
    setIsLoadingDraft(true);
    setLoadDraftFailed(false);
    loadDraft(resumeDraftId)
      .then((loaded) => {
        if (cancelled) return;
        setForm(loaded);
        setIsDirty(false);
        setSaveMessage(null);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadDraftFailed(true);
        toast.error('Failed to load draft');
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoadingDraft(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, resumeDraftId, loadDraft, setClientId]);

  const canGoNextFromStep1 = useMemo(
    () => isCompanyDetailsValid(form.companyDetails),
    [form.companyDetails],
  );
  const canSubmit = useMemo(
    () => canGoNextFromStep1 && isIndividualsPanelValid(form.individuals),
    [canGoNextFromStep1, form.individuals],
  );
  const submitGapHint = useMemo(
    () => (step === 3 && !canSubmit ? describeIndividualsPanelGap(form.individuals) : null),
    [step, canSubmit, form.individuals],
  );

  function confirmClose() {
    if (!isDirty) {
      setStep(1);
      setForm(emptyFormState());
      setIsDirty(false);
      onClose();
      return;
    }
    const ok = window.confirm(
      'Close the onboarding form? Unsaved changes since the last "Save as draft" will be lost.',
    );
    if (ok) {
      setStep(1);
      setForm(emptyFormState());
      setIsDirty(false);
      onClose();
    }
  }

  async function handleSaveDraft() {
    const companyName = form.companyDetails.companyName.trim();
    const countryCode = form.companyDetails.countryCode.trim();

    if (!companyName) {
      setSaveMessage({
        kind: 'warning',
        text: 'Add a company name before saving the draft.',
      });
      return;
    }
    if (countryCode.length !== 2) {
      setSaveMessage({
        kind: 'warning',
        text: 'Pick a country of incorporation before saving the draft.',
      });
      return;
    }

    try {
      const revisionAtSaveStart = changeRevisionRef.current;
      const id = await saveDraft(form);
      // Only clear dirtiness if no panel edits landed while the save
      // was in flight — otherwise the operator's newer edits would be
      // silently swallowed and the close-prompt skipped.
      if (changeRevisionRef.current === revisionAtSaveStart) {
        setIsDirty(false);
      }
      setSaveMessage({ kind: 'success', text: `Draft saved as #${id}` });
      onCreated();
    } catch {
      setSaveMessage({ kind: 'error', text: 'Failed to save draft.' });
    }
  }

  async function handleSubmit() {
    if (!canSubmit) {
      toast.error('Fix the highlighted issues before submitting.');
      return;
    }
    try {
      await submit(form);
      toast.success('Client submitted for KYC review');
      setStep(1);
      setForm(emptyFormState());
      setIsDirty(false);
      setSaveMessage(null);
      onCreated();
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to submit';
      toast.error(msg);
    }
  }

  return (
    <Dialog.Root
      open={open}
      // Intercept outside-click / escape with a confirmation.
      onOpenChange={(next) => {
        if (!next) confirmClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-[95vw] max-w-4xl -translate-x-1/2 -translate-y-1/2',
            'max-h-[90vh] overflow-hidden rounded-xl bg-white shadow-2xl',
            'focus:outline-none',
          )}
          onEscapeKeyDown={(event) => {
            event.preventDefault();
            confirmClose();
          }}
          onPointerDownOutside={(event) => {
            event.preventDefault();
            confirmClose();
          }}
          onInteractOutside={(event) => {
            event.preventDefault();
            confirmClose();
          }}
        >
          <div className="flex items-center justify-between border-b border-[#e8e8e8] px-6 py-4">
            <Dialog.Title className="text-lg font-semibold text-[#333]">
              New business client
              {clientId ? (
                <span className="ml-2 text-xs font-normal text-[#999]">(draft #{clientId})</span>
              ) : null}
            </Dialog.Title>
            <button
              onClick={confirmClose}
              className="rounded-md p-1 text-[#999] transition-colors hover:bg-[#f0f0f0] hover:text-[#333]"
              aria-label="Close"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M4 4l8 8M12 4l-8 8" />
              </svg>
            </button>
          </div>

          {/* Stepper */}
          <div className="flex items-center gap-3 border-b border-[#e8e8e8] bg-[#fafafa] px-6 py-3 text-xs">
            {STEPS.map((s, idx) => (
              <div key={s.id} className="flex items-center gap-2">
                <span
                  className={cn(
                    'flex h-6 w-6 items-center justify-center rounded-full font-semibold',
                    step === s.id
                      ? 'bg-[#6B0F1A] text-white'
                      : step > s.id
                        ? 'bg-[#c8e6c9] text-[#2e7d32]'
                        : 'bg-[#e0e0e0] text-[#999]',
                  )}
                >
                  {s.id}
                </span>
                <span className={cn(step === s.id ? 'font-semibold text-[#333]' : 'text-[#777]')}>
                  {s.label}
                </span>
                {idx < STEPS.length - 1 && <span className="mx-2 h-px w-8 bg-[#e0e0e0]" />}
              </div>
            ))}
          </div>

          <div className="max-h-[60vh] overflow-y-auto px-6 py-5">
            {isLoadingDraft && (
              <div className="py-10 text-center text-sm text-[#999]">Loading draft…</div>
            )}
            {loadDraftFailed && !isLoadingDraft && (
              <div className="py-10 text-center text-sm text-[#c62828]">
                Failed to load draft. Please close and try again.
              </div>
            )}
            {!isLoadingDraft && step === 1 && (
              <CompanyDetailsPanel
                value={form.companyDetails}
                onChange={(v) => {
                  setForm({ ...form, companyDetails: v });
                  changeRevisionRef.current += 1;
                  setIsDirty(true);
                  setSaveMessage(null);
                }}
                errors={fieldErrors}
              />
            )}
            {!isLoadingDraft && step === 2 && (
              <AccountUsagePanel
                value={form.accountUsage}
                onChange={(v) => {
                  setForm({ ...form, accountUsage: v });
                  changeRevisionRef.current += 1;
                  setIsDirty(true);
                  setSaveMessage(null);
                }}
              />
            )}
            {!isLoadingDraft && step === 3 && (
              <IndividualsPanel
                value={form.individuals}
                onChange={(v) => {
                  setForm({ ...form, individuals: v });
                  changeRevisionRef.current += 1;
                  setIsDirty(true);
                  setSaveMessage(null);
                }}
                errors={fieldErrors}
              />
            )}
          </div>

          <div className="flex items-center justify-between border-t border-[#e8e8e8] px-6 py-4">
            <div>
              {step > 1 && !isLoadingDraft && !loadDraftFailed && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setStep((s) => (s - 1) as StepId);
                    setSaveMessage(null);
                  }}
                >
                  ← Back
                </Button>
              )}
            </div>
            <div className="flex items-center gap-3">
              {loadDraftFailed ? (
                <Button variant="outline" onClick={onClose}>
                  Close
                </Button>
              ) : (
                <>
                  {saveMessage && (
                    <span
                      className={cn(
                        'text-xs',
                        saveMessage.kind === 'success' && 'text-[#2e7d32]',
                        (saveMessage.kind === 'warning' || saveMessage.kind === 'error') &&
                          'text-[#c62828]',
                      )}
                      role="status"
                      aria-live="polite"
                    >
                      {saveMessage.text}
                    </span>
                  )}
                  <Button
                    variant="outline"
                    onClick={handleSaveDraft}
                    disabled={isSaving || isSubmitting || isLoadingDraft}
                  >
                    {isSaving ? 'Saving…' : 'Save as draft'}
                  </Button>
                  {step < 3 ? (
                    <Button
                      variant="primary"
                      disabled={(step === 1 && !canGoNextFromStep1) || isSaving || isLoadingDraft}
                      onClick={() => {
                        setStep((s) => (s + 1) as StepId);
                        setSaveMessage(null);
                      }}
                    >
                      Next →
                    </Button>
                  ) : (
                    <div className="flex items-center gap-3">
                      {submitGapHint && (
                        <span
                          id={submitGapHintId}
                          className="text-xs text-[#c62828]"
                          role="status"
                          aria-live="polite"
                        >
                          {submitGapHint}
                        </span>
                      )}
                      <Button
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={!canSubmit || isSubmitting || isLoadingDraft}
                        aria-describedby={submitGapHint ? submitGapHintId : undefined}
                      >
                        {isSubmitting ? 'Submitting…' : 'Submit'}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
